import { createContext, useState, useEffect, useContext } from "react";

import FormulationPickerContext from "./FormulationPickerContext";
import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const DilutionContext = createContext();

export default DilutionContext;

export const DilutionProvider = ({ children }) => {
  const api = useAxios();
  const { 
    formulationsObj, setFormulationsObj, formulationsArray, 
    setFormulationsArray, setFormulationFormErrors 
  } = useContext(FormulationPickerContext);

  const initValues = {
    dilutionBufferIdArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const dilutionObj = {
    dilutionBufferId: null,
    dilutionBufferAcronym: '',
    dilutionFactor: '',
    payloadConcentrationAfterDilution: '',
    volumeAfterDilution: '',
  };
  const [dilutionData, setDilutionData] = useState(dilutionObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/buffer/list/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          dilutionBufferIdArray: response.data,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Dilution Init Data Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (formulationsObj?.ids.length === 0) {
      errors['formulation'] = `${formulationsObj?.type} is required!`;
      setFormulationFormErrors(errors);
    }
    if (!dilutionData?.dilutionBufferId) {
      errors['dilutionBufferId'] = 'Dilution Buffer is required!';
    }
    if (!dilutionData?.dilutionFactor) {
      errors['dilutionFactor'] = 'Dilution factor is required!';
    }
    if (!dilutionData?.payloadConcentrationAfterDilution) {
      errors['payloadConcentrationAfterDilution'] = 'Payload concentration after dilution is required!';
    }
    if (!dilutionData?.volumeAfterDilution) {
      errors['volumeAfterDilution'] = 'Volume after dilution is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      createDilution();
    }
  }, [formErrors]);

  useEffect(() => {  
    const bufferAcronym = initialValues?.dilutionBufferIdArray.find(
      item => item.unique_id === dilutionData?.dilutionBufferId
    )?.buffer_acronym ?? '';

    setDilutionData(prev => ({
      ...prev,
      dilutionBufferAcronym: bufferAcronym
    }));
  }, [dilutionData?.dilutionBufferId]);

  const createDilution = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/dilution/create', {
        formulation_type: formulationsObj?.type,
        formulation_id_list: formulationsObj?.ids.map(item => +item.id),
        dilution_buffer_id: dilutionData?.dilutionBufferId,
        dilution_factor: dilutionData?.dilutionFactor,
        payload_concentration_after_dilution: dilutionData?.payloadConcentrationAfterDilution,
        volume_after_dilution: dilutionData?.volumeAfterDilution,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Dilution create',
        loading: false,
      });
    }
  };

  const contextData = {
    dilutionData: dilutionData,
    setDilutionData: setDilutionData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    formulationsObj: formulationsObj,
    setFormulationsObj: setFormulationsObj,
    formulationsArray: formulationsArray,
    setFormulationsArray: setFormulationsArray,
    initialValues: initialValues,
  };

  return (
    <DilutionContext.Provider value={contextData}>
      {children}
    </DilutionContext.Provider>
  )
};